import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3,
  Blocks,
  ChevronDown,
  ClipboardList,
  FileCheck2,
  FileText,
  FilePlus2,
  FileSpreadsheet,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  X
} from "lucide-react";
import { modulesForUser, OPERATOR_AUTOMATION_BASE, PURCHASE_INVOICES_BASE } from "../app/moduleRegistry";
import { useAuth } from "../app/AuthContext";

const operatorNav = [
  { to: OPERATOR_AUTOMATION_BASE, label: "Tilannekuva", icon: BarChart3, end: true },
  { to: `${OPERATOR_AUTOMATION_BASE}/new`, label: "Uusi saatavuuskysely", icon: FilePlus2 },
  { to: `${OPERATOR_AUTOMATION_BASE}/open`, label: "Avoimet kyselyt", icon: ClipboardList },
  { to: `${OPERATOR_AUTOMATION_BASE}/results`, label: "Tulokset", icon: FileCheck2 },
  { to: `${OPERATOR_AUTOMATION_BASE}/logs`, label: "Lokit / virheet", icon: ScrollText },
  { to: `${OPERATOR_AUTOMATION_BASE}/settings`, label: "Oletusasetukset", icon: Settings }
];

const purchaseInvoiceNav = [
  { to: `${PURCHASE_INVOICES_BASE}/canon`, label: "Canon", icon: FileText, end: false },
  { to: `${PURCHASE_INVOICES_BASE}/idid`, label: "iDiD", icon: FileSpreadsheet, end: false },
  { to: `${PURCHASE_INVOICES_BASE}/applixure`, label: "Applixure", icon: FileText, end: false },
  { to: `${PURCHASE_INVOICES_BASE}/cuuma`, label: "Cuuma", icon: FileText, end: false },
  { to: `${PURCHASE_INVOICES_BASE}/operator-invoices`, label: "Operaattorilaskut", icon: FileSpreadsheet, end: false }
];

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const moduleSwitcherRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, logout } = useAuth();
  const inOperatorModule = location.pathname.startsWith(OPERATOR_AUTOMATION_BASE);
  const inPurchaseInvoicesModule = location.pathname.startsWith(PURCHASE_INVOICES_BASE);
  const accessibleModules = modulesForUser(user);
  const activeModule = accessibleModules.find((module) => module.path && location.pathname.startsWith(module.path));
  const nav = inPurchaseInvoicesModule ? purchaseInvoiceNav : inOperatorModule ? operatorNav : [];

  useEffect(() => setModuleMenuOpen(false), [location.pathname]);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!moduleSwitcherRef.current?.contains(event.target as Node)) setModuleMenuOpen(false);
    };
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setModuleMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", closeWithKeyboard);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", closeWithKeyboard);
    };
  }, []);

  return (
    <div className="app-shell">
      <button className="mobile-menu" aria-label="Avaa valikko" onClick={() => setOpen(true)}>
        <Menu />
      </button>
      {open && <button className="drawer-backdrop" aria-label="Sulje valikko" onClick={() => setOpen(false)} />}

      <aside className={`sidenav ${open ? "sidenav--open" : ""}`}>
        <div className="brand">
          <Link className="brand__home" to="/" onClick={() => setOpen(false)}>
            <img className="brand__logo" src="/elmonet-logo-valkoinen.webp" alt="ElmoNet" />
            <span className="brand__product">
              <span className="allistamo-mark" aria-hidden="true" />
              <span>Ällistämö Express</span>
            </span>
          </Link>
          <button className="sidenav__close" aria-label="Sulje valikko" onClick={() => setOpen(false)}>
            <X />
          </button>
        </div>

        <nav aria-label="Päänavigaatio">
          <span className="nav-section-label">ÄLLISTÄMÖ EXPRESS</span>
          <NavLink to="/" end onClick={() => setOpen(false)}>
            <Blocks size={19} />
            <span>Etusivu</span>
          </NavLink>
          <NavLink to="/settings" onClick={() => setOpen(false)}>
            <Settings size={19} />
            <span>Asetukset</span>
          </NavLink>

          {accessibleModules.length > 0 && <><span className="nav-section-label nav-section-label--module">AKTIIVINEN MODUULI</span>
          <div className="module-switcher-wrap" ref={moduleSwitcherRef}>
            <button
              className={`module-switcher ${moduleMenuOpen ? "module-switcher--open" : ""}`}
              type="button"
              aria-label={`Vaihda moduulia. Aktiivinen moduuli: ${activeModule?.name ?? "Valitse moduuli"}`}
              aria-haspopup="menu"
              aria-expanded={moduleMenuOpen}
              onClick={() => setModuleMenuOpen((current) => !current)}
            >
              <span className="module-switcher__mark">{activeModule?.shortName ?? "AX"}</span>
              <span>
                <strong>{activeModule?.name ?? "Valitse moduuli"}</strong>
                <small>{activeModule ? (inPurchaseInvoicesModule ? "Talousprosessit" : "Elisa-operaattoriprosessi") : "Käyttöoikeutesi"}</small>
              </span>
              <ChevronDown />
            </button>
            {moduleMenuOpen && <div className="module-menu" role="menu" aria-label="Valitse moduuli">
              {accessibleModules.map((module) => (
                <NavLink key={module.id} to={module.path} end role="menuitem" onClick={() => { setOpen(false); setModuleMenuOpen(false); }}>
                  <span>{module.shortName}</span>
                  <span><strong>{module.name}</strong><small>{module.id === "purchase-invoices" ? "Talousprosessit" : "Elisa-operaattoriprosessi"}</small></span>
                </NavLink>
              ))}
            </div>}
          </div>
          </>}

          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}>
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="environment">
          <span className="environment__dot" />
          <div>
            <strong>Tuotantoympäristö</strong>
            <span>Azure</span>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div className="topbar__brand">
            <span className="allistamo-mark allistamo-mark--topbar" aria-hidden="true" />
            <div>
              <span className="topbar__eyebrow">ÄLLISTÄMÖ EXPRESS</span>
              <span className="topbar__title">
                {inOperatorModule ? "Saatavuuskyselyt / Elisa" : inPurchaseInvoicesModule ? "Laskudatan käsittely" : location.pathname === "/settings" ? "Yleiset asetukset" : "ElmoNetin työjonot"}
              </span>
            </div>
          </div>
          <div className="topbar-actions">
            <div className="simulation-pill">
              <span />
              {user?.name ?? user?.email}
            </div>
            <button className="icon-button" type="button" title="Kirjaudu ulos" aria-label="Kirjaudu ulos" onClick={logout}>
              <LogOut />
            </button>
          </div>
        </header>
        <div className="page">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
